/** Japan Life service categories, guides, and slugs */

export const JAPAN_LIFE_CATEGORIES = [
  {
    id: 'settle',
    name: 'Settling In',
    icon: '🏠',
    services: [
      { slug: 'housing',    name: 'Housing search',   icon: '🏘️' },
      { slug: 'guarantor',  name: 'Guarantor help',   icon: '🤝' },
      { slug: 'moving',     name: 'Moving services',  icon: '📦' },
    ],
  },
  {
    id: 'legal',
    name: 'Legal & Visa',
    icon: '📋',
    services: [
      { slug: 'visa',       name: 'Visa & status',    icon: '🛂' },
      { slug: 'nenkin',     name: 'Pension refund',   icon: '💴' },
    ],
  },
  {
    id: 'finance',
    name: 'Money',
    icon: '💰',
    services: [
      { slug: 'banking',    name: 'Bank account',     icon: '🏦' },
      { slug: 'tax',        name: 'Tax filing',       icon: '🧾' },
      { slug: 'remittance', name: 'Send money home',  icon: '💸' },
    ],
  },
  {
    id: 'jobs',
    name: 'Work',
    icon: '💼',
    services: [
      { slug: 'jobs',       name: 'Full-time jobs',   icon: '👔', api: 'jobs', type: 'fulltime' },
      { slug: 'baito',      name: 'Part-time (baito)', icon: '⏰', api: 'jobs', type: 'baito' },
    ],
  },
  {
    id: 'lifestyle',
    name: 'Lifestyle',
    icon: '🌸',
    services: [
      { slug: 'swahili',    name: 'Swahili lessons',  icon: '📚' },
      { slug: 'tours',      name: 'Kenya tours',      icon: '✈️' },
      { slug: 'cars',       name: 'Car rental',       icon: '🚗' },
    ],
  },
];

export const JAPAN_LIFE_GUIDES = {
  housing: {
    title: 'Finding housing in Japan',
    steps: [
      'Gather key money: deposit (shikikin), key money (reikin), agency fee — often 4–6 months rent upfront.',
      'Get a guarantor (保証人) — company or guarantor service; many landlords require this for foreigners.',
      'Search on Suumo, Homes, or ask in the mudiaspora community for agent referrals.',
      'Visit in person if possible; check train commute and building rules (pets, instruments).',
      'Sign contract; register address at ward office within 14 days of moving.',
    ],
    tips: ['Share apartments (シェアハウス) are easier for new arrivals.', 'Company housing (社宅) if your employer offers it.'],
  },
  guarantor: {
    title: 'Guarantor services',
    steps: [
      'If you have no Japanese guarantor, use a guarantor company (GTN, Casa, etc.).',
      'Fee is typically 50–100% of one month\'s rent per year.',
      'Your employer may act as guarantor for company transfers.',
      'Ask senior Kenyans in your prefecture — community members often advise on trusted services.',
    ],
    tips: ['Some share houses and foreigner-friendly agencies skip guarantor requirements.'],
  },
  visa: {
    title: 'Visa & residence status',
    steps: [
      'Check your residence card (在留カード) expiry — renew 3 months before expiration.',
      'Gather documents: employment certificate, tax records, passport photos.',
      'Book immigration appointment or apply by mail depending on renewal type.',
      'For status changes (e.g. student → work), consult immigration or a licensed advisor.',
    ],
    tips: ['Keep pension and tax records organised — needed for many visa renewals.', 'Community members offering visa help are listed below.'],
  },
  banking: {
    title: 'Opening a bank account',
    steps: [
      'You need residence card, passport, and proof of address (utility bill or residence certificate).',
      'Japan Post Bank and Shinsei/Sony Bank are often foreigner-friendly.',
      'Visit branch in person; some banks require Japanese phone number.',
      'Set up online banking and cash card (キャッシュカード).',
    ],
    tips: ['Seven Bank ATMs work with many foreign cards before you get a local account.'],
  },
  tax: {
    title: 'Tax basics for residents',
    steps: [
      'Residents pay income tax (national) and residence tax (local) on worldwide income.',
      'Employer withholds 源泉徴収; file year-end adjustment (年末調整) if employed.',
      'Side income may require 確定申告 (tax return) Feb–Mar each year.',
      'Keep receipts for deductions; consider a tax advisor for first filing.',
    ],
    tips: ['Nenkin pension statements help with visa renewals and refunds when leaving Japan.'],
  },
  remittance: {
    title: 'Sending money to Kenya',
    steps: [
      'Compare Wise, Remitly, WorldRemit, and bank wire rates — fees and FX spread vary daily.',
      'You need recipient M-Pesa or bank details in Kenya.',
      'Large transfers may require source-of-funds documentation.',
      'Track rate alerts; KES/JPY moves with both economies.',
    ],
    tips: ['Community often shares current best rates in WhatsApp groups.'],
  },
  baito: {
    title: 'Part-time work (baito)',
    steps: [
      'Check visa restrictions — students: 28 hrs/week during term; work visa: check side-job permission.',
      'Search Townwork, Baitoru, Hello Work, and mudiaspora job listings.',
      'Prepare rirekisho (resume) and basic Japanese for interviews.',
      'Convenience store, restaurant, and warehouse roles often hire foreigners.',
    ],
    tips: ['English OK and visa-support jobs are tagged in our job board.'],
  },
  jobs: {
    title: 'Full-time employment',
    steps: [
      'Update LinkedIn, Daijob, GaijinPot, and mudiaspora job listings.',
      'Engineering, nursing, and English teaching are common paths for Kenyans.',
      'Prepare for multiple interview rounds; some companies use Japanese-only interviews.',
      'Confirm visa sponsorship before accepting an offer.',
    ],
    tips: ['Filter jobs below by visa support and English OK.'],
  },
  nenkin: {
    title: 'Pension (nenkin) lump-sum withdrawal',
    steps: [
      'If leaving Japan permanently, you may claim lump-sum withdrawal (脱退一時金).',
      'Must apply within 2 years of leaving; need bank account and pension book.',
      'Amount depends on months contributed; typically 3–36 months of payments eligible.',
      'Apply via Japan Pension Service online or by mail.',
    ],
    tips: ['Keep your basic pension number (基礎年金番号) safe.'],
  },
  swahili: {
    title: 'Swahili in Japan',
    steps: [
      'Connect with Kenyan community teachers listed below.',
      'Useful for kids maintaining language, or teaching Japanese friends.',
      'Online lessons via Zoom are common across prefectures.',
    ],
    tips: ['Offer lessons in your profile to appear as a provider.'],
  },
};

export const BUSINESS_CATEGORIES = [
  'Restaurant', 'Cafe', 'Grocery', 'Barber/Salon', 'Church/Fellowship',
  'Shipping/Import', 'Real estate', 'Tech services', 'Education', 'Other',
];

export function findServiceBySlug(slug) {
  for (const cat of JAPAN_LIFE_CATEGORIES) {
    const svc = cat.services.find(s => s.slug === slug);
    if (svc) return { ...svc, category: cat };
  }
  return null;
}

export function allServiceSlugs() {
  return JAPAN_LIFE_CATEGORIES.flatMap(c => c.services.map(s => s.slug));
}
